import {
  getUnitsForQuantity,
  isNumeric,
} from '@oliasoft-open-source/units';
import { IField, ISite, IWell } from '@oliasoft/hierarchy-gui';
import { extractLocationInputData } from '@oliasoft/gis-core';
import { EntityType } from 'common/enums/entity-enums.js';
import { RecognisedDataCatalogues, RecognisedDataObjects } from 'common/enums/known-data-enums.js';
import {
  collapsePressureMap,
  covarianceMatrixTotalMap,
  surveyProgramMap,
  definitiveSurveyListMap,
  fieldCRSSettingsMap,
  fracturePressureMap,
  geomagneticParametersMap,
  minimumHorizontalStressPressureMap,
  porePressureMap,
  positionalUncertaintyMap,
  projectDataMap,
  surveyRunMap,
  temperatureDefinitionMap,
  temperatureListingMap,
  trajectoryPlanMap,
  uncertaintyVectorsMap,
  wellheadPositionMap,
} from 'common/services/configs/index.js';
import { KnowEdmTableData as Formationdata } from 'common/services/configs/formations/table-enums.js';
import { KnowTableData as UncertaintyData } from 'common/services/configs/uncertainty/table-enums.js';
import { KnowEdmTableData as LocationData } from 'common/services/configs/locations/table-enums.js';
import { isVerticalTable } from 'common/utils/is-vertical-table.js';
import { zip } from 'es-toolkit/compat';
import Papa from 'papaparse';
import { ReactUINodes } from 'common/enums/common.js';
import { holeSizeMap } from 'common/services/configs/trajectories/hole-size.js';
import { siteReferenceSystemMap } from 'common/services/configs/locations/site-reference-system.js';
import { unitFromQuantity } from '@oliasoft-open-source/units';

type TCoordinateReferenceData = { site: ISite, well: IWell, field: IField }

export const createUnitCell = (unit: string, quanity: string | undefined, setUnit: (unit: string) => void) => {
  if (quanity) {
    const units = getUnitsForQuantity(quanity) || [];
    const options = units.map(u => ({
      label: u,
      value: u,
    }));
    return {
      type: 'Select',
      width: '80px',
      options,
      value: {
        label: unit,
        value: unit,
      },
      onChange: (ev: any) => { setUnit(ev.target.value); },
    };
  }
  return {
    value: unit,
  };
};

const replaceWithDot = (string: string) => string.replace(',', '.');
const replaceDataCommasWithDots = (data: string[][]) => data.map(row => row.map(cell => replaceWithDot(cell)));
const formatHeadersMessage = (arr: string[]) => arr.reduce((acc, cur, index, arr) => {
  return `${acc} ${cur}${arr.length - 1 === index ? '' : ','}`;
}, '');

// custom function to guess the delimiter because the one which ships with Papa Parse fails for comma decimals
export const guessDelimiter = (input: string) => {
  const spaceCount = (input.substring(0, 200).match('    ') || []).length;
  const tabCount = (input.substring(0, 200).match(/\t/g) || []).length;
  const semiColonCount = (input.substring(0, 200).match(/;/g) || []).length;

  if (tabCount === 0 && semiColonCount === 0 && spaceCount === 0) {
    return ',';
  }
  if (spaceCount > tabCount && spaceCount > semiColonCount) return '    ';
  return tabCount > semiColonCount ? '\t' : ';';
};

const baseConfig = {
  /*
    Note: parsing CSV returns numbers as strings
    This is intentional, because we want to preserve the user input format (not convert)
    Therefore don't use the dynamicTyping option of PapaParse to try to convert
  */
  skipEmptyLines: true,
  transform: (value: string) => value.trim(),
  delimiter: guessDelimiter,
};

export const transposeTable = (table: any[][]) => zip(...table);

export const entityTableLabelMap = {
  [EntityType.ProjectData]: RecognisedDataCatalogues.ProjectData,
  [EntityType.WellboreWellheadPosition]: RecognisedDataCatalogues.WellboreWellheadPosition,
  [EntityType.CoordinateReferenceSystem]: RecognisedDataCatalogues.CoordinateReferenceSystem,
  [EntityType.PorePressureP10]: RecognisedDataObjects.PorePressureP10,
  [EntityType.PorePressureP50]: RecognisedDataObjects.PorePressureP50,
  [EntityType.PorePressureP90]: RecognisedDataObjects.PorePressureP90,
  [EntityType.FracturePressureP10]: RecognisedDataObjects.FracturePressureP10,
  [EntityType.FracturePressureP50]: RecognisedDataObjects.FracturePressureP50,
  [EntityType.FracturePressureP90]: RecognisedDataObjects.FracturePressureP90,
  [EntityType.CollapsePressureP10]: RecognisedDataObjects.CollapsePressureP10,
  [EntityType.CollapsePressureP50]: RecognisedDataObjects.CollapsePressureP50,
  [EntityType.CollapsePressureP90]: RecognisedDataObjects.CollapsePressureP90,
  [EntityType.MinimumHorizontalStressPressureP10]: RecognisedDataObjects.MinimumHorizontalStressPressureP10,
  [EntityType.MinimumHorizontalStressPressureP50]: RecognisedDataObjects.MinimumHorizontalStressPressureP50,
  [EntityType.MinimumHorizontalStressPressureP90]: RecognisedDataObjects.MinimumHorizontalStressPressureP90,
  [EntityType.TemperatureDefinition]: RecognisedDataObjects.GeothermalTemperatureDefinition,
  [EntityType.TemperatureListing]: RecognisedDataObjects.GeothermalTemperatureListing,
  [EntityType.IPMFile]: RecognisedDataObjects.IPMFile,
  [EntityType.GeomagneticParameters]: RecognisedDataCatalogues.GeomagneticParametersDefinition,
  [EntityType.SurveyRun]: RecognisedDataObjects.SurveyRun,
  [EntityType.DefinitiveSurveyRun]: RecognisedDataCatalogues.DefinitiveSurveyList,
  [EntityType.SurveyProgram]: RecognisedDataCatalogues.SurveyProgram,
  [EntityType.TrajectoryPlan]: RecognisedDataCatalogues.TrajectoryPlan,
  [EntityType.PositionalUncertaintyList]: RecognisedDataCatalogues.WellborePositionUncertainty,
  [EntityType.CovarianceMatrix]: RecognisedDataCatalogues.CovarianceMatrixTotal,
  [EntityType.UncertaintyVectors]: RecognisedDataCatalogues.UncertaintyVectors,
  [EntityType.HoleSize]: RecognisedDataCatalogues.HoleSize,
  [EntityType.SiteReferenceSystem]: RecognisedDataCatalogues.SiteReferenceSystem,
};

export const entityTypeMap = new Map<EntityType, Map<any, any>>();
entityTypeMap.set(EntityType.WellboreWellheadPosition, wellheadPositionMap);
entityTypeMap.set(EntityType.CoordinateReferenceSystem, fieldCRSSettingsMap);
entityTypeMap.set(EntityType.ProjectData, projectDataMap);
entityTypeMap.set(EntityType.PorePressureP10, porePressureMap);
entityTypeMap.set(EntityType.PorePressureP50, porePressureMap);
entityTypeMap.set(EntityType.PorePressureP90, porePressureMap);
entityTypeMap.set(EntityType.FracturePressureP10, fracturePressureMap);
entityTypeMap.set(EntityType.FracturePressureP50, fracturePressureMap);
entityTypeMap.set(EntityType.FracturePressureP90, fracturePressureMap);
entityTypeMap.set(EntityType.CollapsePressureP10, collapsePressureMap);
entityTypeMap.set(EntityType.CollapsePressureP50, collapsePressureMap);
entityTypeMap.set(EntityType.CollapsePressureP90, collapsePressureMap);
entityTypeMap.set(EntityType.MinimumHorizontalStressPressureP10, minimumHorizontalStressPressureMap);
entityTypeMap.set(EntityType.MinimumHorizontalStressPressureP50, minimumHorizontalStressPressureMap);
entityTypeMap.set(EntityType.MinimumHorizontalStressPressureP90, minimumHorizontalStressPressureMap);
entityTypeMap.set(EntityType.TemperatureDefinition, temperatureDefinitionMap);
entityTypeMap.set(EntityType.TemperatureListing, temperatureListingMap);
entityTypeMap.set(EntityType.GeomagneticParameters, geomagneticParametersMap);
entityTypeMap.set(EntityType.SurveyRun, surveyRunMap);
entityTypeMap.set(EntityType.DefinitiveSurveyRun, definitiveSurveyListMap);
entityTypeMap.set(EntityType.SurveyProgram, surveyProgramMap);
entityTypeMap.set(EntityType.TrajectoryPlan, trajectoryPlanMap);
entityTypeMap.set(EntityType.PositionalUncertaintyList, positionalUncertaintyMap);
entityTypeMap.set(EntityType.CovarianceMatrix, covarianceMatrixTotalMap);
entityTypeMap.set(EntityType.UncertaintyVectors, uncertaintyVectorsMap);
entityTypeMap.set(EntityType.HoleSize, holeSizeMap);
entityTypeMap.set(EntityType.SiteReferenceSystem, siteReferenceSystemMap);

export const getHeadersFromEntityType = (type: EntityType, visibleColumns: string[]): string[] => {
  const map = entityTypeMap.get(type);
  return map ? ([...map.values()].filter(value => !visibleColumns?.length || visibleColumns.includes(value?.key)).map(value => value.label)) : [];
};

export const getKeysFromEntityType = (type: EntityType, visibleColumns: string[]): string[] => {
  const map = entityTypeMap.get(type);
  return map ? ([...map.values()].filter(value => !visibleColumns?.length || visibleColumns.includes(value?.key)).map(value => value.key)) : [];
};

export const getSelectNodesFromEntityType = (type: EntityType, visibleColumns: string[]): Record<string, string> => {
  const map = entityTypeMap.get(type);
  return map ? ([...map.values()].filter(value => !visibleColumns?.length || visibleColumns.includes(value?.options)).reduce((acc, cur) => {
    if (cur.type === ReactUINodes.Select) {
      return { ...acc, [cur.key]: cur.type };
    }
    return acc;
  }, {})) : {};
};

export const getQuantitiesFromEntityType = (type: EntityType, visibleColumns: string[]): string[] => {
  const map = entityTypeMap.get(type);
  return map ? ([...map.values()].filter(value => !visibleColumns?.length || visibleColumns.includes(value?.key)).map(value => value.quantity)) : [];
};

export const getUnitsFromEntityType = (type: EntityType, visibleColumns: string[], unitTemplate: any): string[] => {
  const map = entityTypeMap.get(type);
  return map
    ? [...map.values()].reduce((acc, { key, quantity }) => {
      return (!visibleColumns?.length || visibleColumns.includes(key))
        ? [...acc, getPreferredUnit(quantity, unitTemplate)]
        : acc;
    }, [])
    : [];
};

export const getParametersFromEntityType = (type: EntityType, visibleColumns: string[] = []): string[] => {
  const map = entityTypeMap.get(type);
  return map ? ([...map.values()].filter(value => !visibleColumns?.length || visibleColumns.includes(value?.key)).map(value => value.key)) : [];
};

export const getTooltipsFromEntityType = (type: EntityType, visibleColumns: string[]): string[] => {
  const map = entityTypeMap.get(type);
  return map ? ([...map.values()].filter(value => !visibleColumns?.length || visibleColumns.includes(value?.key)).map(value => value.tooltip)) : [];
};

export enum VectorParameters {
  PorePressure = 'porePressure',
  UncertaintyVectorE = 'vectorE',
  UncertaintyVectorEStar = 'vectorEStar',
}
export enum VectorHeaders {
  PorePressure = 'Pore Pressure Vector',
  UncertaintyVectorE = 'e',
  UncertaintyVectorEStar = 'e*',
}

export const getVectorParamMap = (type: EntityType | undefined) => {
  switch (type) {
    case EntityType.PorePressureP10:
      return { [VectorParameters.PorePressure]: [Formationdata.Tvd, Formationdata.PorePressureGradient, Formationdata.PorePressure] } as { [key: string]: string[] };
    case EntityType.UncertaintyVectors:
      return {
        [VectorParameters.UncertaintyVectorE]: [UncertaintyData.VectorEX, UncertaintyData.VectorEY, UncertaintyData.VectorEZ],
        [VectorParameters.UncertaintyVectorEStar]: [UncertaintyData.VectorEStarX, UncertaintyData.VectorEStarY, UncertaintyData.VectorEStarZ],
      } as { [key: string]: string[] };
    default:
      return {};
  }
};

export const getVectorParametersFromEntityType = (type: EntityType, visibleColumns: string[]): string[] => {
  const parameters = getParametersFromEntityType(type, visibleColumns);
  switch (type) {
    case EntityType.PorePressureP10:
      parameters.splice(0, 3, VectorParameters.PorePressure);
      return parameters;
    case EntityType.UncertaintyVectors:
      parameters.splice(2, 6, VectorParameters.UncertaintyVectorE, VectorParameters.UncertaintyVectorEStar);
      return parameters;
    default:
      return [];
  }
};

export const getVectorHeadersFromEntityType = (type: EntityType, visibleColumns: string[]): string[] => {
  const parameters = getHeadersFromEntityType(type, visibleColumns);
  switch (type) {
    case EntityType.PorePressureP10:
      parameters.splice(0, 3, VectorHeaders.PorePressure);
      return parameters;
    case EntityType.UncertaintyVectors:
      parameters.splice(2, 6, VectorHeaders.UncertaintyVectorE, VectorHeaders.UncertaintyVectorEStar);
      return parameters;
    default:
      return [];
  }
};

export const isVectorTable = (type: EntityType): boolean => {
  switch (type) {
    case EntityType.UncertaintyVectors:
      return true;
    default:
      return false;
  }
};

const isSingleRow = (type: EntityType | string): boolean => {
  switch (type) {
    case EntityType.CoordinateReferenceSystem:
      return true;
    default:
      return false;
  }
};

export const parsePlainCSVText = (plainText: string, headers: string[], units: string[], type: EntityType | null): { data: string[][] | { [key: string]: string } | { [key: string]: string }[] } | never => {
  const parsedResult = Papa.parse(plainText, {
    ...baseConfig,
    preview: 5000,
  }) as { data: string[][] };

  let parsedData = parsedResult.data;
  if (type && isVerticalTable(type)) {
    const transposedTable = transposeTable(parsedResult.data);
    // swap data row and unit row to horizontal table format
    parsedData = [transposedTable[0], transposedTable[2], transposedTable[1]];
  }

  const headerRow = parsedData[0];
  const reformattedData = replaceDataCommasWithDots(parsedData);

  const sortedParsedDataHeader = headerRow?.map(i => i.toLocaleLowerCase())?.toSorted();
  const pastedHeadersEqualToOriginal = headers?.map(i => i.toLocaleLowerCase())?.toSorted().every((h, index) => h?.toLowerCase() === sortedParsedDataHeader[index]?.toLowerCase());

  const doesNotHaveHeaders = headerRow?.some(isNumeric);
  const dataRows = doesNotHaveHeaders ? reformattedData : reformattedData.slice(1);
  const firstRowIndex = dataRows.findIndex(row => row.some(Boolean));

  if (!pastedHeadersEqualToOriginal && firstRowIndex > 0) {
    throw new Error(`Headers missmatch expected headers ${formatHeadersMessage(headers)} but got ${formatHeadersMessage(headerRow)}`);
  }

  if (!doesNotHaveHeaders && headers?.length !== headerRow?.length) {
    throw new Error(`Pasted different numbers of headers then expected expected headers ${formatHeadersMessage(headers)} but got ${formatHeadersMessage(headerRow)}`);
  }

  const hasEntityType = type && Object.values(EntityType).includes(type);
  if (hasEntityType) {
    if (firstRowIndex < 0) {
      throw new Error('Missing data');
    }
    const rowParameters = getParametersFromEntityType(type);

    const reduceData = (acc: object, value: string, index: number) => (value && value !== '' ? { ...acc, [rowParameters[index]]: value } : acc);
    const data = reformattedData.slice(firstRowIndex + 1).reduce((acc: any[], row) => {
      if (!row.every(value => !value)) {
        const reducedRow = row.reduce(reduceData, {});
        acc.push(reducedRow);
      }
      return acc;
    }, []);

    if (isSingleRow(type)) {
      data.push([]);
    }
    const objectData = {
      ...parsedData,
      data,
    };

    return objectData;
  }
  const reformattedResult = {
    ...parsedData,
    data: reformattedData,
  };

  if (doesNotHaveHeaders) {
    // do not verify headers
    const resultWithDefaultHaders = {
      ...reformattedResult,
      data: [headers, units, ...reformattedResult.data],
    };
    return resultWithDefaultHaders;
  }
  return reformattedResult;
};

const getWellheadLinkableData = ({ site, well }: TCoordinateReferenceData) => {
  //@ts-ignore
  const locationData = extractLocationInputData(well.settings.coordinates, site.settings.coordinates);
  const {
    offsetEasting, offsetNorthing, longitudeDD, easting, northing, latitudeDD, locationEPSG
  } = locationData;

  return {
    [LocationData.EPSGCode]: locationEPSG,
    [LocationData.GeographicalLongitude]: longitudeDD,
    [LocationData.GeographicalLatitude]: latitudeDD,
    [LocationData.Easting]: easting,
    [LocationData.Northing]: northing,
    [LocationData.LocalOffsetEast]: offsetEasting,
    [LocationData.LocalOffsetNorth]: offsetNorthing,
  };
};

const getFieldReferenceSystemData = ({ field }: TCoordinateReferenceData) => {
  //@ts-ignore
  const locationData = extractLocationInputData(field.settings.coordinates, field.settings.coordinates);
  const {
    datumLabel, locationEPSG, mapSystemLabel, locationLabel, offsetEasting, offsetNorthing, longitudeDD, easting, northing, referenceLocationType, distanceToReference, latitudeDD,
  } = locationData;
  return {
    [LocationData.GeodeticDatum]: datumLabel,
    [LocationData.EPSGCode]: locationEPSG,
    [LocationData.MapSystem]: mapSystemLabel,
    [LocationData.MapZone]: locationLabel,
    [LocationData.LocalPositionEW]: offsetEasting,
    [LocationData.LocalPositionNS]: offsetNorthing,
    [LocationData.GeographicalLongitude]: longitudeDD,
    [LocationData.MapPositionEW]: easting,
    [LocationData.MapPositionNS]: northing,
    [LocationData.ReferencePositionType]: referenceLocationType,
    [LocationData.DistanceToReferencePosition]: distanceToReference,
    [LocationData.GeographicalLatitude]: latitudeDD,
  };
};

const getSiteReferenceSystemData = ({ site }: TCoordinateReferenceData) => {
  // @ts-ignore
  const locationData = extractLocationInputData(site.settings.coordinates, site.settings.coordinates);
  const {
    datumLabel, locationEPSG, mapSystemLabel, locationLabel, longitudeDD, easting, northing, referenceLocationType, distanceToReference, latitudeDD,
  } = locationData;

  return {
    [LocationData.GeodeticDatum]: datumLabel,
    [LocationData.EPSGCode]: locationEPSG,
    [LocationData.MapSystem]: mapSystemLabel,
    [LocationData.MapZone]: locationLabel,
    [LocationData.GeographicalLongitude]: longitudeDD,
    [LocationData.Easting]: easting,
    [LocationData.Northing]: northing,
    [LocationData.ReferencePositionType]: referenceLocationType,
    [LocationData.DistanceToReferencePosition]: distanceToReference,
    [LocationData.GeographicalLatitude]: latitudeDD,
  };
};

export const linkableEntityTablesMap = (type: EntityType, { site, well, field }: TCoordinateReferenceData) => {
  const map: Partial<Record<EntityType, any>> = {
    [EntityType.WellboreWellheadPosition]: getWellheadLinkableData,// ref from well since wellbore has no coordinates
    [EntityType.CoordinateReferenceSystem]: getFieldReferenceSystemData, // ref from field
    [EntityType.SiteReferenceSystem]: getSiteReferenceSystemData,  // ref from site
  };

  if (map[type]) {
    return map[type]({ site, well, field });
  }
  throw new Error(`${type}, not implemented`);
};

/**
 * Retrieves the preferred unit for a given quantity from a specified unit template or defaults to a standard unit if not found.
 */
export const getPreferredUnit = (quantity: string, unitTemplate: { [key: string]: string }): string | undefined => {
  return unitTemplate?.[quantity] ?? unitFromQuantity(quantity);
};
